import screenService from './services/ScreenService.js'
import deviceService from './services/DeviceService.js'
import channelService from './services/ChannelService.js'

class AIService {
  constructor() {
    this.analytics = {}
    this.systemData = {}
  }

  // Initialize AI service with system data
  async initialize(userId) {
    if (!userId) return
    
    try {
      // Fetch all relevant data with error handling
      let devices = [], screens = [], schedules = [], channels = []
      
      try {
        devices = await this.fetchDevices(userId)
      } catch (error) {
        console.warn('Could not fetch devices:', error.message)
        devices = []
      }
      
      try {
        screens = await this.fetchScreens(userId)
      } catch (error) {
        console.warn('Could not fetch screens:', error.message)
        screens = []
      }
      
      try {
        schedules = await this.fetchSchedules(userId)
      } catch (error) {
        console.warn('Could not fetch schedules:', error.message)
        schedules = []
      }

      try {
        channels = await this.fetchChannels()
      } catch (error) {
        console.warn('Could not fetch channels:', error.message)
        channels = []
      }

      this.systemData = { devices, screens, schedules, channels }
      this.analytics = this.generateAnalytics(devices, screens, schedules, channels)
      
      return { devices, screens, schedules, channels, analytics: this.analytics }
    } catch (error) {
      console.error('Error initializing AI service:', error)
      // Return default data instead of throwing
      const defaultData = { devices: [], screens: [], schedules: [], channels: [] }
      this.systemData = defaultData
      this.analytics = this.generateAnalytics([], [], [], [])
      return { ...defaultData, analytics: this.analytics }
    }
  }

  // Fetch devices data
  async fetchDevices(userId) {
    try {
      return await deviceService.getUserDevices(userId, false) // Force fresh data
    } catch (error) {
      console.warn('Error fetching devices:', error.message)
      return []
    }
  }

  // Fetch screens data
  async fetchScreens(userId) {
    try {
      return await screenService.getUserScreens(userId, false) // Force fresh data
    } catch (error) {
      console.warn('Error fetching screens:', error.message)
      return []
    }
  }

  // Fetch channels data
  async fetchChannels() {
    try {
      return await channelService.getAllChannels({}, false) // Force fresh data
    } catch (error) {
      console.warn('Error fetching channels:', error.message)
      return []
    }
  }

  // Fetch schedules data from device properties
  async fetchSchedules(userId) {
    try {
      // Schedules are stored as properties within each device object
      // We'll extract them from the devices data instead of a separate collection
      const devices = await deviceService.getUserDevices(userId, false)
      
      const schedules = []
      devices.forEach(device => {
        if (device.schedule && Array.isArray(device.schedule)) {
          // Add device reference to each schedule
          device.schedule.forEach(schedule => {
            // Determine schedule type based on isRepetitive flag
            const scheduleType = schedule.isRepetitive ? 'repetitive' : 'regular'
            
            schedules.push({
              ...schedule,
              deviceId: device.key,
              deviceName: device.name || 'Unknown Device',
              type: scheduleType
            })
          })
        }
      })
      
      return schedules
    } catch (error) {
      console.warn('Error fetching schedules from devices:', error.message)
      return []
    }
  }

  // Generate comprehensive analytics
  generateAnalytics(devices, screens, schedules, channels) {
    const onlineDevices = devices.filter(device => 
      device.lastAccess && (Date.now() - device.lastAccess < 360000)
    ).length

    // Calculate total schedules and categorize by type
    const scheduleStats = devices.reduce((stats, device) => {
      if (device.schedule && Array.isArray(device.schedule)) {
        device.schedule.forEach(schedule => {
          // Determine schedule type based on isRepetitive flag
          const type = schedule.isRepetitive ? 'repetitive' : 'regular'
          stats.total++
          stats.byType[type] = (stats.byType[type] || 0) + 1
        })
      }
      return stats
    }, { total: 0, byType: {} })

    // Channel analytics
    const channelStats = {
      total: channels.length,
      active: channels.filter(channel => channel.isActive).length,
      muted: channels.filter(channel => channel.isMuted).length,
      premium: channels.filter(channel => channel.isPremium).length,
      devicesUsingChannels: devices.filter(device => device.channelId && device.channelId !== '').length,
      devicesUsingScreens: devices.filter(device => device.screenId && device.screenId !== '' && (!device.channelId || device.channelId === '')).length
    }

    const screenTypes = screens.reduce((acc, screen) => {
      const type = screen.type || 'unknown'
      acc[type] = (acc[type] || 0) + 1
      return acc
    }, {})

    const deviceHealth = devices.map(device => {
      const deviceSchedules = device.schedule ? device.schedule.length : 0
      const repetitiveSchedules = device.schedule ? 
        device.schedule.filter(s => s.isRepetitive === true).length : 0
      const regularSchedules = device.schedule ? 
        device.schedule.filter(s => s.isRepetitive === false).length : 0

      return {
        id: device.key,
        name: device.name,
        status: device.lastAccess && (Date.now() - device.lastAccess < 360000) ? 'online' : 'offline',
        lastSeen: device.lastAccess ? new Date(device.lastAccess) : null,
        schedules: deviceSchedules,
        repetitiveSchedules,
        regularSchedules,
        contentType: device.channelId ? 'channel' : device.screenId ? 'screen' : 'none'
      }
    })

    return {
      totalDevices: devices.length,
      onlineDevices,
      offlineDevices: devices.length - onlineDevices,
      totalScreens: screens.length,
      totalChannels: channelStats.total,
      activeChannels: channelStats.active,
      mutedChannels: channelStats.muted,
      premiumChannels: channelStats.premium,
      devicesUsingChannels: channelStats.devicesUsingChannels,
      devicesUsingScreens: channelStats.devicesUsingScreens,
      totalSchedules: scheduleStats.total,
      repetitiveSchedules: scheduleStats.byType.repetitive || 0,
      regularSchedules: scheduleStats.byType.regular || 0,
      onlinePercentage: devices.length > 0 ? Math.round((onlineDevices / devices.length) * 100) : 0,
      channelUtilization: devices.length > 0 ? Math.round((channelStats.devicesUsingChannels / devices.length) * 100) : 0,
      screenUtilization: devices.length > 0 ? Math.round((channelStats.devicesUsingScreens / devices.length) * 100) : 0,
      avgSchedulesPerDevice: devices.length > 0 ? Math.round((scheduleStats.total / devices.length) * 10) / 10 : 0,
      avgRepetitivePerDevice: devices.length > 0 ? Math.round(((scheduleStats.byType.repetitive || 0) / devices.length) * 10) / 10 : 0,
      avgRegularPerDevice: devices.length > 0 ? Math.round(((scheduleStats.byType.regular || 0) / devices.length) * 10) / 10 : 0,
      screenTypes,
      deviceHealth,
      performanceScore: this.calculatePerformanceScore(devices, screens, schedules, channels),
      schedulesAvailable: scheduleStats.total > 0,
      channelsAvailable: channelStats.total > 0
    }
  }

  // Calculate overall performance score
  calculatePerformanceScore(devices, screens, schedules, channels) {
    let score = 0
    
    // Device connectivity (30% weight)
    const onlinePercentage = devices.length > 0 ? 
      devices.filter(d => d.lastAccess && (Date.now() - d.lastAccess < 360000)).length / devices.length : 0
    score += onlinePercentage * 30

    // Content utilization (25% weight) - now includes both screens and channels
    const totalContent = screens.length + channels.length
    const contentUtilization = totalContent > 0 ? Math.min(totalContent / Math.max(devices.length, 1), 1) : 0
    score += contentUtilization * 25

    // Channel utilization (20% weight) - new metric
    const channelUtilization = devices.length > 0 ? 
      devices.filter(d => d.channelId && d.channelId !== '').length / devices.length : 0
    score += channelUtilization * 20

    // Schedule optimization (25% weight) - consider both repetitive and regular schedules
    const scheduleStats = devices.reduce((stats, device) => {
      if (device.schedule && Array.isArray(device.schedule)) {
        device.schedule.forEach(schedule => {
          // Determine schedule type based on isRepetitive flag
          const type = schedule.isRepetitive ? 'repetitive' : 'regular'
          stats.total++
          stats.byType[type] = (stats.byType[type] || 0) + 1
        })
      }
      return stats
    }, { total: 0, byType: {} })

    const avgSchedules = devices.length > 0 ? scheduleStats.total / devices.length : 0
    const repetitiveRatio = scheduleStats.total > 0 ? (scheduleStats.byType.repetitive || 0) / scheduleStats.total : 0
    
    // Higher score for balanced schedule types (mix of repetitive and regular)
    const scheduleScore = Math.min(avgSchedules / 5, 1) * (0.7 + 0.3 * Math.min(repetitiveRatio, 0.5) * 2)
    score += scheduleScore * 25

    return Math.round(score)
  }

  // Generate content suggestions
  generateContentSuggestions(context = 'general') {
    const suggestions = {
      restaurant: [
        {
          type: 'menu',
          title: 'Daily Specials Menu',
          description: 'Highlight today\'s special dishes with appetizing images',
          template: 'menu-template',
          priority: 'high'
        },
        {
          type: 'promotion',
          title: 'Happy Hour Banner',
          description: 'Promote happy hour deals with eye-catching design',
          template: 'promotion-template',
          priority: 'medium'
        },
        {
          type: 'info',
          title: 'Opening Hours',
          description: 'Display current opening hours and contact information',
          template: 'info-template',
          priority: 'low'
        }
      ],
      retail: [
        {
          type: 'promotion',
          title: 'Sale Banner',
          description: 'Promote current sales and discounts',
          template: 'sale-template',
          priority: 'high'
        },
        {
          type: 'product',
          title: 'Featured Products',
          description: 'Showcase featured products with pricing',
          template: 'product-template',
          priority: 'medium'
        }
      ],
      general: [
        {
          type: 'info',
          title: 'Welcome Message',
          description: 'Create a welcoming atmosphere for visitors',
          template: 'welcome-template',
          priority: 'medium'
        },
        {
          type: 'promotion',
          title: 'Event Promotion',
          description: 'Promote upcoming events or announcements',
          template: 'event-template',
          priority: 'high'
        }
      ]
    }

    return suggestions[context] || suggestions.general
  }

  // Generate channel suggestions
  generateChannelSuggestions(context = 'general') {
    const suggestions = {
      restaurant: [
        {
          type: 'news',
          title: 'Local News Channel',
          description: 'Keep customers informed with local news and updates',
          category: 'information',
          priority: 'medium'
        },
        {
          type: 'weather',
          title: 'Weather Updates',
          description: 'Display current weather conditions and forecasts',
          category: 'information',
          priority: 'low'
        },
        {
          type: 'sports',
          title: 'Sports Highlights',
          description: 'Show sports news and highlights for entertainment',
          category: 'entertainment',
          priority: 'medium'
        }
      ],
      retail: [
        {
          type: 'news',
          title: 'Business News',
          description: 'Display financial markets and business updates',
          category: 'information',
          priority: 'high'
        },
        {
          type: 'lifestyle',
          title: 'Lifestyle Channel',
          description: 'Show lifestyle and fashion content to inspire customers',
          category: 'entertainment',
          priority: 'medium'
        },
        {
          type: 'weather',
          title: 'Weather Channel',
          description: 'Keep customers informed about weather conditions',
          category: 'information',
          priority: 'low'
        }
      ],
      general: [
        {
          type: 'news',
          title: 'News Channel',
          description: '24/7 news coverage with latest updates',
          category: 'information',
          priority: 'high'
        },
        {
          type: 'weather',
          title: 'Weather Updates',
          description: 'Real-time weather updates and forecasts',
          category: 'information',
          priority: 'medium'
        },
        {
          type: 'sports',
          title: 'Sports Network',
          description: 'Live sports coverage and highlights',
          category: 'entertainment',
          priority: 'medium'
        },
        {
          type: 'music',
          title: 'Music Channel',
          description: 'Non-stop music videos and entertainment',
          category: 'entertainment',
          priority: 'low'
        }
      ]
    }

    return suggestions[context] || suggestions.general
  }

  // Generate optimization recommendations
  generateOptimizationRecommendations() {
    const recommendations = []
    const analytics = this.analytics

    // Device connectivity recommendations
    if (analytics.onlinePercentage < 80) {
      recommendations.push({
        type: 'device_connectivity',
        priority: 'high',
        title: 'Improve Device Connectivity',
        description: `${analytics.offlineDevices} devices are offline. Check power and network connections.`,
        action: 'check_devices',
        impact: 'high'
      })
    }

    // Channel optimization recommendations
    if (analytics.totalChannels === 0) {
      recommendations.push({
        type: 'channel_optimization',
        priority: 'medium',
        title: 'Create Your First Channel',
        description: 'Channels provide live content streaming to multiple devices simultaneously.',
        action: 'create_channels',
        impact: 'medium'
      })
    } else if (analytics.activeChannels === 0) {
      recommendations.push({
        type: 'channel_optimization',
        priority: 'medium',
        title: 'Activate Your Channels',
        description: 'You have channels but none are currently active. Activate them to start streaming.',
        action: 'activate_channels',
        impact: 'medium'
      })
    } else if (analytics.channelUtilization < 30) {
      recommendations.push({
        type: 'channel_optimization',
        priority: 'low',
        title: 'Increase Channel Usage',
        description: `Only ${analytics.channelUtilization}% of devices are using channels. Consider assigning channels to more devices.`,
        action: 'assign_channels',
        impact: 'low'
      })
    }

    // Content strategy recommendations
    if (analytics.devicesUsingScreens > analytics.devicesUsingChannels) {
      recommendations.push({
        type: 'content_strategy',
        priority: 'medium',
        title: 'Consider Channel Strategy',
        description: 'Most devices are using screens. Consider using channels for live content streaming.',
        action: 'explore_channels',
        impact: 'medium'
      })
    } else if (analytics.devicesUsingChannels > analytics.devicesUsingScreens) {
      recommendations.push({
        type: 'content_strategy',
        priority: 'low',
        title: 'Balance Content Types',
        description: 'Most devices are using channels. Consider adding some screen content for variety.',
        action: 'add_screens',
        impact: 'low'
      })
    }

    // Content optimization recommendations
    if (analytics.avgSchedulesPerDevice < 2) {
      recommendations.push({
        type: 'content_optimization',
        priority: 'medium',
        title: 'Increase Content Variety',
        description: 'Add more scheduled content to improve engagement.',
        action: 'create_schedules',
        impact: 'medium'
      })
    }

    // Performance recommendations
    if (analytics.performanceScore < 70) {
      recommendations.push({
        type: 'performance',
        priority: 'high',
        title: 'System Performance Optimization',
        description: 'Overall system performance needs improvement.',
        action: 'optimize_system',
        impact: 'high'
      })
    }

    return recommendations
  }

  // Generate intelligent scheduling suggestions
  generateSchedulingSuggestions() {
    const suggestions = []
    const analytics = this.analytics

    // Peak time scheduling
    suggestions.push({
      type: 'peak_times',
      title: 'Peak Time Content',
      description: 'Schedule important content during peak viewing hours (9-11 AM, 4-6 PM)',
      schedule: {
        startTime: '09:00',
        endTime: '11:00',
        days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        priority: 'high'
      }
    })

    // Content rotation
    suggestions.push({
      type: 'rotation',
      title: 'Content Rotation',
      description: 'Implement content rotation every 2-3 hours to maintain engagement',
      schedule: {
        rotationInterval: 7200, // 2 hours in seconds
        priority: 'medium'
      }
    })

    // Maintenance windows
    suggestions.push({
      type: 'maintenance',
      title: 'Maintenance Windows',
      description: 'Schedule system updates during low-traffic hours',
      schedule: {
        startTime: '02:00',
        endTime: '04:00',
        days: ['sunday'],
        priority: 'low'
      }
    })

    return suggestions
  }

  // Generate performance insights
  generatePerformanceInsights() {
    const insights = []
    const analytics = this.analytics

    // Device health insights
    if (analytics.onlinePercentage >= 90) {
      insights.push({
        type: 'positive',
        category: 'device_health',
        title: 'Excellent Device Connectivity',
        description: 'Your devices are performing exceptionally well with high uptime.',
        metric: `${analytics.onlinePercentage}% uptime`
      })
    } else if (analytics.onlinePercentage < 70) {
      insights.push({
        type: 'warning',
        category: 'device_health',
        title: 'Device Connectivity Issues',
        description: 'Several devices are offline. Immediate attention recommended.',
        metric: `${analytics.offlineDevices} devices offline`
      })
    }

    // Content utilization insights
    if (analytics.totalScreens > analytics.totalDevices) {
      insights.push({
        type: 'positive',
        category: 'content',
        title: 'Good Content Variety',
        description: 'You have more content than devices, ensuring good rotation.',
        metric: `${analytics.totalScreens} screens for ${analytics.totalDevices} devices`
      })
    }

    // Performance score insights
    if (analytics.performanceScore >= 80) {
      insights.push({
        type: 'positive',
        category: 'overall',
        title: 'High Performance Score',
        description: 'Your digital signage system is performing excellently.',
        metric: `${analytics.performanceScore}/100 score`
      })
    }

    return insights
  }

  // Create automated content
  async createAutomatedContent(userId, type, data) {
    try {
      const contentData = {
        uid: userId,
        type: type,
        name: data.name || `AI Generated ${type}`,
        content: data.content,
        settings: data.settings || {},
        created: Date.now(),
        aiGenerated: true
      }

      const result = await screenService.createScreen(contentData)
      return { success: true, id: result.key, data: result }
    } catch (error) {
      console.error('Error creating automated content:', error)
      throw error
    }
  }

  // Generate content based on template
  generateContentFromTemplate(template, data = {}) {
    const templates = {
      'menu-template': {
        type: 'image',
        content: {
          background: '#ffffff',
          elements: [
            {
              type: 'text',
              content: data.title || 'Daily Specials',
              style: {
                fontSize: '48px',
                fontWeight: 'bold',
                color: '#2d3748',
                textAlign: 'center',
                marginBottom: '20px'
              }
            },
            {
              type: 'text',
              content: data.description || 'Discover our chef\'s special creations',
              style: {
                fontSize: '24px',
                color: '#718096',
                textAlign: 'center'
              }
            }
          ]
        }
      },
      'promotion-template': {
        type: 'image',
        content: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          elements: [
            {
              type: 'text',
              content: data.title || 'Special Offer',
              style: {
                fontSize: '56px',
                fontWeight: 'bold',
                color: '#ffffff',
                textAlign: 'center',
                marginBottom: '10px'
              }
            },
            {
              type: 'text',
              content: data.description || 'Limited time only!',
              style: {
                fontSize: '32px',
                color: '#e2e8f0',
                textAlign: 'center'
              }
            }
          ]
        }
      }
    }

    return templates[template] || templates['menu-template']
  }

  // Get real-time system status
  getSystemStatus() {
    const analytics = this.analytics
    const status = {
      overall: analytics.performanceScore >= 80 ? 'excellent' : 
               analytics.performanceScore >= 60 ? 'good' : 
               analytics.performanceScore >= 40 ? 'fair' : 'poor',
      devices: analytics.onlinePercentage >= 90 ? 'healthy' : 
               analytics.onlinePercentage >= 70 ? 'warning' : 'critical',
      content: analytics.totalScreens > 0 || analytics.totalChannels > 0 ? 'active' : 'inactive',
      channels: analytics.totalChannels > 0 ? 'available' : 'none',
      schedules: analytics.totalSchedules > 0 ? 'configured' : 'empty'
    }

    return {
      status,
      summary: this.generateStatusSummary(status),
      alerts: this.generateAlerts(status)
    }
  }

  // Generate status summary
  generateStatusSummary(status) {
    const summaries = {
      excellent: 'Your digital signage system is performing excellently with optimal connectivity and content utilization.',
      good: 'Your system is performing well with minor areas for improvement.',
      fair: 'Your system needs attention in several areas to improve performance.',
      poor: 'Your system requires immediate attention to resolve critical issues.'
    }

    return summaries[status.overall] || summaries.fair
  }

  // Generate alerts
  generateAlerts(status) {
    const alerts = []

    if (status.devices === 'critical') {
      alerts.push({
        type: 'critical',
        title: 'Device Connectivity Critical',
        message: 'Multiple devices are offline. Check network and power connections.',
        action: 'check_devices'
      })
    }

    if (status.content === 'inactive') {
      alerts.push({
        type: 'warning',
        title: 'No Content Available',
        message: 'No screens or channels are configured. Create content to start displaying.',
        action: 'create_content'
      })
    }

    if (status.channels === 'none') {
      alerts.push({
        type: 'info',
        title: 'No Channels Available',
        message: 'Consider adding live channels for real-time content streaming.',
        action: 'explore_channels'
      })
    }

    if (status.schedules === 'empty') {
      alerts.push({
        type: 'info',
        title: 'No Schedules Configured',
        message: 'Set up content schedules to automate your displays.',
        action: 'create_schedules'
      })
    }

    return alerts
  }

  // Delete screen by name
  async deleteScreenByName(userId, screenName) {
    try {
      // Find the screen by name using ScreenService
      const screen = await screenService.getScreenByName(userId, screenName, false)
      
      if (!screen) {
        return { success: false, error: `Screen "${screenName}" not found` }
      }
      
      // Use ScreenService to properly delete the screen
      await screenService.deleteScreen(screen.key, userId)
      
      return { success: true, message: `Screen "${screenName}" deleted successfully` }
    } catch (error) {
      console.error('Error deleting screen:', error)
      return { success: false, error: error.message }
    }
  }

  // Delete device by name
  async deleteDeviceByName(userId, deviceName) {
    try {
      // Find the device by name using DeviceService
      const devices = await deviceService.getUserDevices(userId, false)
      const device = devices.find(d => d.name === deviceName)
      
      if (!device) {
        return { success: false, error: `Device "${deviceName}" not found` }
      }
      
      // Use DeviceService to properly delete the device
      await deviceService.deleteDevice(device.key)
      
      return { success: true, message: `Device "${deviceName}" deleted successfully` }
    } catch (error) {
      console.error('Error deleting device:', error)
      return { success: false, error: error.message }
    }
  }

  // Find screen by name (partial match)
  findScreenByName(screenName) {
    const screens = this.systemData.screens || []
    const lowerScreenName = screenName.toLowerCase()
    
    // Exact match first
    let exactMatch = screens.find(screen => 
      screen.name.toLowerCase() === lowerScreenName
    )
    if (exactMatch) return exactMatch
    
    // Partial match
    let partialMatch = screens.find(screen => 
      screen.name.toLowerCase().includes(lowerScreenName)
    )
    if (partialMatch) return partialMatch
    
    // Multiple partial matches
    let multipleMatches = screens.filter(screen => 
      screen.name.toLowerCase().includes(lowerScreenName)
    )
    if (multipleMatches.length > 1) return multipleMatches
    
    return null
  }

  // Find device by name (partial match)
  findDeviceByName(deviceName) {
    const devices = this.systemData.devices || []
    const lowerDeviceName = deviceName.toLowerCase()
    
    // Exact match first
    let exactMatch = devices.find(device => 
      device.name.toLowerCase() === lowerDeviceName
    )
    if (exactMatch) return exactMatch
    
    // Partial match
    let partialMatch = devices.find(device => 
      device.name.toLowerCase().includes(lowerDeviceName)
    )
    if (partialMatch) return partialMatch
    
    // Multiple partial matches
    let multipleMatches = devices.filter(device => 
      device.name.toLowerCase().includes(lowerDeviceName)
    )
    if (multipleMatches.length > 1) return multipleMatches
    
    return null
  }

  // Find channel by name (partial match)
  findChannelByName(channelName) {
    const channels = this.systemData.channels || []
    const lowerChannelName = channelName.toLowerCase()
    
    // Exact match first
    let exactMatch = channels.find(channel => 
      channel.name.toLowerCase() === lowerChannelName
    )
    if (exactMatch) return exactMatch
    
    // Partial match
    let partialMatch = channels.find(channel => 
      channel.name.toLowerCase().includes(lowerChannelName)
    )
    if (partialMatch) return partialMatch
    
    // Multiple partial matches
    let multipleMatches = channels.filter(channel => 
      channel.name.toLowerCase().includes(lowerChannelName)
    )
    if (multipleMatches.length > 1) return multipleMatches
    
    return null
  }

  // Get channel analytics
  getChannelAnalytics() {
    if (!this.systemData.channels) return null
    
    const channels = this.systemData.channels
    const devices = this.systemData.devices || []
    
    return {
      total: channels.length,
      active: channels.filter(ch => ch.isActive).length,
      muted: channels.filter(ch => ch.isMuted).length,
      premium: channels.filter(ch => ch.isPremium).length,
      devicesUsingChannels: devices.filter(d => d.channelId && d.channelId !== '').length,
      averageDevicesPerChannel: channels.length > 0 ? 
        devices.filter(d => d.channelId && d.channelId !== '').length / channels.length : 0,
      mostPopularChannel: this.getMostPopularChannel(),
      channelCategories: this.getChannelCategories()
    }
  }

  // Get most popular channel (by device usage)
  getMostPopularChannel() {
    if (!this.systemData.channels || !this.systemData.devices) return null
    
    const deviceCounts = {}
    this.systemData.devices.forEach(device => {
      if (device.channelId && device.channelId !== '') {
        deviceCounts[device.channelId] = (deviceCounts[device.channelId] || 0) + 1
      }
    })
    
    const mostUsedChannelId = Object.keys(deviceCounts).reduce((a, b) => 
      deviceCounts[a] > deviceCounts[b] ? a : b, null
    )
    
    if (mostUsedChannelId) {
      return this.systemData.channels.find(ch => ch.key === mostUsedChannelId)
    }
    
    return null
  }

  // Get channel categories
  getChannelCategories() {
    if (!this.systemData.channels) return {}
    
    const categories = {}
    this.systemData.channels.forEach(channel => {
      const category = channel.category || 'general'
      categories[category] = (categories[category] || 0) + 1
    })
    
    return categories
  }

  // Generate channel management suggestions
  generateChannelManagementSuggestions() {
    const analytics = this.analytics
    const suggestions = []
    
    if (analytics.totalChannels === 0) {
      suggestions.push({
        type: 'creation',
        title: 'Create Your First Channel',
        description: 'Start with a news or weather channel to provide live content to your devices.',
        priority: 'high',
        action: 'create_channel'
      })
    } else if (analytics.activeChannels === 0) {
      suggestions.push({
        type: 'activation',
        title: 'Activate Your Channels',
        description: 'Activate your channels to start streaming live content to your devices.',
        priority: 'medium',
        action: 'activate_channels'
      })
    } else if (analytics.mutedChannels > 0) {
      suggestions.push({
        type: 'unmute',
        title: 'Unmute Channels',
        description: `${analytics.mutedChannels} channels are muted. Consider unmuting them for better engagement.`,
        priority: 'low',
        action: 'unmute_channels'
      })
    }
    
    if (analytics.devicesUsingChannels < analytics.totalDevices * 0.5) {
      suggestions.push({
        type: 'assignment',
        title: 'Assign Channels to More Devices',
        description: 'Consider assigning channels to more devices to increase live content coverage.',
        priority: 'medium',
        action: 'assign_channels'
      })
    }
    
    return suggestions
  }

  // Get content strategy insights
  getContentStrategyInsights() {
    const analytics = this.analytics
    
    return {
      strategy: analytics.devicesUsingChannels > analytics.devicesUsingScreens ? 'channel-focused' : 'screen-focused',
      channelUtilization: analytics.channelUtilization,
      screenUtilization: analytics.screenUtilization,
      recommendations: this.generateContentStrategyRecommendations()
    }
  }

  // Generate content strategy recommendations
  generateContentStrategyRecommendations() {
    const analytics = this.analytics
    const recommendations = []
    
    if (analytics.devicesUsingChannels === 0 && analytics.devicesUsingScreens === 0) {
      recommendations.push({
        type: 'initial_setup',
        title: 'Set Up Your First Content',
        description: 'Start by creating either a screen or channel to display content on your devices.',
        priority: 'high'
      })
    } else if (analytics.devicesUsingChannels === 0) {
      recommendations.push({
        type: 'add_channels',
        title: 'Add Live Channels',
        description: 'Consider adding live channels for real-time content streaming alongside your screens.',
        priority: 'medium'
      })
    } else if (analytics.devicesUsingScreens === 0) {
      recommendations.push({
        type: 'add_screens',
        title: 'Add Custom Screens',
        description: 'Consider adding custom screens for more personalized content alongside your channels.',
        priority: 'medium'
      })
    } else {
      recommendations.push({
        type: 'optimize_balance',
        title: 'Optimize Content Balance',
        description: 'You have a good mix of channels and screens. Consider optimizing based on your audience preferences.',
        priority: 'low'
      })
    }
    
    return recommendations
  }

  // Refresh specific data type
  async refreshData(dataType, userId) {
    if (!userId) return
    
    try {
      switch (dataType) {
        case 'devices':
          const devices = await this.fetchDevices(userId)
          this.systemData.devices = devices
          break
        case 'screens':
          const screens = await this.fetchScreens(userId)
          this.systemData.screens = screens
          break
        case 'channels':
          const channels = await this.fetchChannels()
          this.systemData.channels = channels
          break
        case 'schedules':
          const schedules = await this.fetchSchedules(userId)
          this.systemData.schedules = schedules
          break
        default:
          // Refresh all data
          await this.initialize(userId)
          return
      }
      
      // Regenerate analytics with updated data
      this.analytics = this.generateAnalytics(
        this.systemData.devices || [],
        this.systemData.screens || [],
        this.systemData.schedules || [],
        this.systemData.channels || []
      )
    } catch (error) {
      console.error(`Error refreshing ${dataType} data:`, error)
    }
  }
}

export default AIService 